import React, { Component, Fragment } from "react";
import { renderToString } from "react-dom/server";
import PropTypes from "prop-types";

import Widget from "../../components/Widget";
import Time from "../../components/Time";
import DashboardEmptyState from "./DashboardEmptyState";
import WidgetHelpLink from "./WidgetHelpLink";
import LegendWithSelection from "./LegendWithSelection";
import WidgetTotalsItem from "./WidgetTotalsItem";

import reportApi from "../../api/reportApi";

import { convertDateForChart, formatTrendText, transformDataDateForDemo, getDaysDiff } from "./dashboardHelper";
import { getMessage } from "../../messages";
import WidgetDateRange from "../../components/WidgetDateRange";
import { LOYALTY_ENROLLMENT_HELP_LINK } from "../../constants/dashboardConstants";

const ENROLLMENT_TYPE = {
  LEAD: "lead",
  CUSTOMER: "customer"
};

const CHART_COLORS = {
  TOTAL: "#759ECC",
  CUSTOMER: "#3AAAD3",
  LEAD: "#B2D1F2"
};

const CHART_HEIGHT = 164;

class EnrollmentsWidget extends Component {
  constructor(props) {
    super(props);

    this.chartMetaData = [
      {
        id: ENROLLMENT_TYPE.LEAD,
        displayName: getMessage("enrollmentsWidget.chartMetaData.lead.displayName"),
        color: CHART_COLORS.LEAD
      },
      {
        id: ENROLLMENT_TYPE.CUSTOMER,
        displayName: getMessage("enrollmentsWidget.chartMetaData.customer.displayName"),
        color: CHART_COLORS.CUSTOMER
      }
    ];

    const legendSelection = {};
    this.chartMetaData.map((item) => {
      legendSelection[item.id] = true;
    });

    const chartSeriesArr = this.chartMetaData.map((d) => {
      return {
        id: d.id,
        name: d.displayName,
        color: d.color,
        data: []
      };
    });

    this.state = {
      loading: true,
      selectedDaysRange: getDaysDiff(props.initialStartDate, props.initialEndDate),
      dateRange: {
        startDateStr: props.initialStartDate,
        endDateStr: props.initialEndDate
      },
      chartSeriesArr,
      legendSelection,
      totals: {},
      chartCustomTooltip: [],
      showEmptyState: false
    };
  }

  componentDidMount() {
    this.loadData(this.state.dateRange);
  }

  loadData(dateRange) {
    const promises = [reportApi.getEnrollmentsData(dateRange), reportApi.getEnrollmentsTotals(dateRange)];

    this.setState({ loading: true, showEmptyState: false });

    Promise.all(promises)
      .then((response) => {
        this.setChartSeriesArrState(response[0]);
        this.setTotalsState(response[1]);
      })
      .catch((error) => {
        this.setState({ showEmptyState: true });
        throw error;
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  }

  getNumberValueAttr() {
    return { humanize: "auto", precision: "2", "trim-fraction-zeros": "true" };
  }

  getPctValueAttr() {
    return { humanize: "auto", precision: "1", type: "percent", "trim-fraction-zeros": "true" };
  }

  getTrendText(value, daysRange) {
    return formatTrendText`${value} than previous ${daysRange} days`;
  }

  getChartTooltipContent(date, { total, dataPerType }) {
    let content = [];

    const dateElm = renderToString(<Time value={date} onlyDate={true} />);

    content.push({ label: dateElm, type: "header" });

    content.push({
      value: renderToString(
        <Fragment>
          <e-numeric value={total || 0}></e-numeric>
        </Fragment>
      ),
      label: getMessage("enrollmentsWidget.tooltip.label"),
      color: CHART_COLORS.TOTAL
    });

    this.chartMetaData.forEach((metaData) => {
      const { num, pct } = { ...dataPerType[metaData.id] };
      content.push({
        value: renderToString(
          <Fragment>
            <e-numeric value={num || 0}></e-numeric> (<e-numeric value={pct || 0} type="percent"></e-numeric>)
          </Fragment>
        ),
        label: metaData.displayName,
        color: metaData.color
      });
    });

    return content;
  }

  getItemsForLegend() {
    return this.chartMetaData.map((item) => {
      return {
        id: item.id,
        displayName: item.displayName,
        color: item.color,
        isSelected: this.state.legendSelection[item.id]
      };
    });
  }

  setChartSeriesArrState(dataRows) {
    if (!dataRows) {
      this.setState({ showEmptyState: true });
      return;
    }

    dataRows = transformDataDateForDemo(dataRows, this.state.selectedDaysRange);

    let { chartSeriesArr } = { ...this.state };

    let chartCustomTooltip = {};

    //reset data on each series
    chartSeriesArr.forEach((series) => {
      series.data = [];
    });

    dataRows.forEach((row) => {
      const date = convertDateForChart(row.date);
      chartSeriesArr.forEach((series) => {
        series.data.push({
          x: date,
          y: row.dataPerType[series.id].num || 0
        });
      });

      chartCustomTooltip[date] = { rows: this.getChartTooltipContent(date, row) };
    });

    this.setState({ chartSeriesArr, chartCustomTooltip });
  }

  setTotalsState({ total, totalPrevDiffPct, dataPerType }) {
    if (!dataPerType) {
      this.setState({ showEmptyState: true });
      return;
    }

    const customerTotals = dataPerType[ENROLLMENT_TYPE.CUSTOMER];
    const leadTotals = dataPerType[ENROLLMENT_TYPE.LEAD];
    let totals = {
      total,
      totalPrevDiffPct,
      customerTotal: customerTotals.total || 0,
      customerTotalPrevDiffPct: customerTotals.totalPrevDiffPct || 0,
      leadTotal: leadTotals.total || 0,
      leadTotalPrevDiffPct: leadTotals.totalPrevDiffPct || 0
    };

    this.setState({ totals });
  }

  onTimeRangeChange(value) {
    const { start, end } = value;
    let dateRange = {
      startDateStr: start,
      endDateStr: end
    };
    this.setState({ dateRange, selectedDaysRange: getDaysDiff(start, end) });
    this.loadData(dateRange);
  }

  onLegendSelectionChange(selectionId) {
    let { legendSelection } = { ...this.state };

    legendSelection[selectionId] = !legendSelection[selectionId];

    this.setState({ legendSelection });
  }

  renderTotals() {
    const daysRange = this.state.selectedDaysRange;
    const { total, totalPrevDiffPct, customerTotal, customerTotalPrevDiffPct, leadTotal, leadTotalPrevDiffPct } = {
      ...this.state.totals
    };
    return (
      <div className="e-grid">
        <div className="e-cell e-cell-4">
          <WidgetTotalsItem
            title={getMessage("enrollmentsWidget.totals.enrollTotal.title")}
            value={total || 0}
            valueNumericAttr={this.getNumberValueAttr()}
            valueColor={CHART_COLORS.TOTAL}
            trendValue={totalPrevDiffPct || 0}
            trendText={this.getTrendText(totalPrevDiffPct, daysRange)}
          />
        </div>
        <div className="e-cell e-cell-4">
          <WidgetTotalsItem
            title={getMessage("enrollmentsWidget.totals.customerTotal.title")}
            value={customerTotal}
            valueNumericAttr={this.getNumberValueAttr()}
            valueColor={CHART_COLORS.CUSTOMER}
            trendValue={customerTotalPrevDiffPct}
            trendText={this.getTrendText(customerTotalPrevDiffPct, daysRange)}
          />
        </div>
        <div className="e-cell e-cell-4">
          <WidgetTotalsItem
            title={getMessage("enrollmentsWidget.totals.leadTotal.title")}
            value={leadTotal}
            valueNumericAttr={this.getNumberValueAttr()}
            valueColor={CHART_COLORS.LEAD}
            trendValue={leadTotalPrevDiffPct}
            trendText={this.getTrendText(leadTotalPrevDiffPct, daysRange)}
          />
        </div>
      </div>
    );
  }

  renderChart() {
    const { chartSeriesArr, chartCustomTooltip, legendSelection } = { ...this.state };

    return (
      <ec-chart height={CHART_HEIGHT} domain-type="time" custom-tooltip={JSON.stringify(chartCustomTooltip)}>
        <ec-series-stack>
          {chartSeriesArr.map((series, index) => {
            if (legendSelection[series.id]) {
              return (
                <ec-series-stack-area
                  key={series.id}
                  order={index}
                  name={series.name}
                  data={JSON.stringify(series.data)}
                  color={series.color}
                ></ec-series-stack-area>
              );
            }
          })}
        </ec-series-stack>
      </ec-chart>
    );
  }

  renderContent() {
    const { showEmptyState } = { ...this.state };

    if (showEmptyState) {
      return <DashboardEmptyState />;
    }

    return (
      <Fragment>
        <div>{this.renderTotals()}</div>
        <div className="e-margin-top-m">{this.renderChart()}</div>
        <div className="e-margin-top-m">
          <LegendWithSelection
            items={this.getItemsForLegend()}
            onChange={this.onLegendSelectionChange.bind(this)}
            visible={true}
            disableClick={true}
          />
        </div>
      </Fragment>
    );
  }

  render() {
    return (
      <Widget
        title={getMessage("enrollmentsWidget.title")}
        className="enrollments-widget"
        headerActions={
          <Fragment>
            <WidgetDateRange
              onChange={this.onTimeRangeChange.bind(this)}
              isDemoMode={false}
              dateRange={{ start: this.state.dateRange.startDateStr, end: this.state.dateRange.endDateStr }}
            />
          </Fragment>
        }
        footerActions={<WidgetHelpLink linkUrl={LOYALTY_ENROLLMENT_HELP_LINK} />}
        content={this.renderContent()}
        loading={this.state.loading}
        isEmptyState={this.state.showEmptyState}
      />
    );
  }
}

EnrollmentsWidget.propTypes = {
  tiers: PropTypes.array,
  initialStartDate: PropTypes.string,
  initialEndDate: PropTypes.string
};

export default EnrollmentsWidget;
